import { useMemo, useState } from "react";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import {
  classifyTickerEntry,
  splitEntries,
  type TickerCheck,
} from "@/pages/PortfolioBuilder/builderModel";
import {
  buildTickerIndex,
  isKnownTicker,
  suggestTickers,
} from "@/pages/PortfolioBuilder/tickerSuggestions";
import MatchBox from "@/pages/PortfolioBuilder/components/builder/MatchBox";
import ChipInput from "@/pages/PortfolioBuilder/components/builder/ChipInput";
import TermChip, { CheckPanel, FlagChip } from "@/pages/PortfolioBuilder/components/builder/TermChip";
import CsvImportStep from "@/pages/PortfolioBuilder/components/builder/CsvImportStep";
import { isSymbolEntry } from "../portfolioEntries";
import { downloadPortfolioTemplate } from "../portfolioCsv";
import { HINT, SECTION_LABEL } from "../ui/theme";

interface PortfolioQuickAddProps {
  /** Symbols in capitals, and words kept as typed — see portfolioEntries.ts. */
  tickers: string[];
  onChange: (next: string[]) => void;
}

/**
 * The names the user actually holds, asked for at the very start.
 *
 * Second on the page rather than last: it is the most specific thing anyone
 * can tell us, and the only answer here that produces a watchlist of things
 * they own rather than a category they trade. Under the markets grid it read
 * as a footnote to a set of checkboxes.
 *
 * The same box as the watchlist builder's Tickers box, with its rules: a
 * symbol goes in as written, with no lookup; a name, or a short word that
 * could be either, is held back as "William ?" to check; a CSV goes through
 * the same check step. There's no Save here to hold back, so a word left
 * unchecked is kept as a keyword — and the box says so.
 */
export default function PortfolioQuickAdd({ tickers: entries, onChange }: PortfolioQuickAddProps) {
  const [draft, setDraft] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  /** Words the user chose to keep as keywords, so they stop asking. */
  const [kept, setKept] = useState<string[]>([]);
  /** Which check is open: undefined means the first one; null, none. */
  const [openCheck, setOpenCheck] = useState<string | null | undefined>(undefined);

  const { portfolios } = usePortfoliosCRUD();
  const index = useMemo(() => buildTickerIndex(portfolios), [portfolios]);
  const isKnown = (symbol: string) => isKnownTicker(index, symbol);

  const symbols = entries.filter(isSymbolEntry);
  const words = entries.filter((e) => !isSymbolEntry(e));
  const keptWords = words.filter((w) => kept.includes(w));
  const checks: TickerCheck[] = words
    .filter((w) => !kept.includes(w))
    .map((word) => {
      const read = classifyTickerEntry(word, () => false);
      return { word, reason: read?.kind === "check" ? read.reason : "name" };
    });
  const activeCheck =
    openCheck === undefined
      ? checks[0]?.word
      : openCheck && checks.some((c) => c.word === openCheck)
        ? openCheck
        : undefined;

  /** Adds entries once each: symbols by value, words in any case. */
  const add = (next: string[]) => {
    const out = [...entries];
    for (const entry of next) {
      const lower = entry.toLowerCase();
      if (isSymbolEntry(entry) ? out.includes(entry) : out.some((e) => e.toLowerCase() === lower)) {
        continue;
      }
      out.push(entry);
    }
    if (out.length !== entries.length) onChange(out);
  };

  const commit = (text: string) => {
    const read = splitEntries(text)
      .map((entry) => classifyTickerEntry(entry, isKnown))
      .flatMap((r) => (r ? [r.kind === "ticker" ? r.symbol : r.word] : []));
    add(read);
    setDraft("");
    setOpenCheck(undefined);
  };

  const replace = (word: string, next?: string) =>
    onChange(
      entries.flatMap((e) => (e === word ? (next && !entries.includes(next) ? [next] : []) : [e]))
    );

  const remove = (entry: string) => {
    onChange(entries.filter((e) => e !== entry));
    setKept((prev) => prev.filter((w) => w !== entry));
  };

  const open = checks.find((c) => c.word === activeCheck);
  const suggestions = suggestTickers(index, draft, { exclude: symbols }).map((symbol) => ({
    key: symbol,
    label: symbol,
  }));

  return (
    <div>
      {/* No icon. Two of the three sections carried one and the third did
          not, which read as decoration rather than meaning. */}
      <span className={SECTION_LABEL}>Your own portfolio</span>
      <p className={`${HINT} mt-0.5`}>These become a watchlist, highlighted everywhere.</p>

      <div className="mt-2">
        <MatchBox
          label="Tickers you hold"
          title="Tickers you hold"
          action={
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded text-xs text-squawk-blue hover:text-squawk-blue/80 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-squawk-blue/70">
              <input
                type="file"
                accept=".csv,text/csv,.txt"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setCsvFile(file);
                  e.target.value = "";
                }}
              />
              <ArrowUpTrayIcon aria-hidden="true" className="size-3.5" />
              Upload CSV
            </label>
          }
          field={
            <>
              {symbols.map((symbol) => (
                <TermChip key={symbol} kind="ticker" label={symbol} onRemove={() => remove(symbol)} />
              ))}
              {keptWords.map((word) => (
                <TermChip key={word} kind="keyword" label={word} onRemove={() => remove(word)} />
              ))}
              {checks.map((check) => (
                <FlagChip
                  key={check.word}
                  word={check.word}
                  open={activeCheck === check.word}
                  onClick={() => setOpenCheck(activeCheck === check.word ? null : check.word)}
                />
              ))}
              <ChipInput
                value={draft}
                onChange={setDraft}
                suggestions={suggestions}
                onCommit={commit}
                onPick={(s) => {
                  add([s.label]);
                  setDraft("");
                }}
                // Committing on blur too: people type the last ticker and
                // reach straight for Continue, and losing it there would be
                // silent.
                placeholder={
                  entries.length > 0 ? "Add ticker symbols" : "Add ticker symbols, e.g. AAPL, BRK.B"
                }
                ariaLabel="Add ticker symbols"
              />
            </>
          }
        >
          {open && (
            <CheckPanel
              message={
                open.reason === "unknown"
                  ? `Is “${open.word}” a ticker?`
                  : `“${open.word}” doesn’t look like a ticker.`
              }
              actions={[
                ...(open.reason === "unknown"
                  ? [
                      {
                        label: "Keep it as a ticker",
                        onClick: () => replace(open.word, open.word.toUpperCase()),
                        primary: true,
                      },
                    ]
                  : []),
                {
                  label: "Make it a keyword",
                  onClick: () => {
                    setKept((prev) => [...prev, open.word]);
                    setOpenCheck(undefined);
                  },
                  primary: open.reason !== "unknown",
                },
                { label: "Remove", onClick: () => remove(open.word) },
              ]}
            />
          )}
          {checks.length > 0 && (
            <p className="m-0 text-xs text-gray-400">
              Anything left unchecked is matched as a keyword.
            </p>
          )}
          {csvFile && (
            <CsvImportStep
              file={csvFile}
              onAdd={(rows) => {
                add(rows);
                setCsvFile(null);
              }}
              onCancel={() => setCsvFile(null)}
            />
          )}
        </MatchBox>
      </div>

      <button
        type="button"
        onClick={downloadPortfolioTemplate}
        className="mt-1.5 cursor-pointer rounded border-none bg-transparent p-0 text-xs text-squawk-blue transition-colors hover:text-squawk-blue/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
      >
        Download a CSV template
      </button>
    </div>
  );
}
